'use client';

import Cookies from 'js-cookie';
import { usePathname } from 'next/navigation';
import { useContext, useEffect, useRef } from 'react';

import {
    CartContext,
    Cookie,
    decodeJwt,
    getOrigin,
    PageType,
    useCustomerAutoshipSubscriptions,
    useLoyaltyContext,
    usePageLayout,
    useUserSession,
} from '@libs/common-next';
import { AUTH_COOKIES, COOKIES, getOktaIdToken } from '@libs/core';

import { dataLayerPush, trackError } from '../gtm';
import {
    clearFreeShippingMeterStorage,
    getFreeShippingMeterAnalyticsValue,
    getStoredFreeShippingMeterValue,
    isFreeShippingMeterLockedForConfirmation,
    pageDataMapper,
    syncFreeShippingMeterStorage,
    unlockFreeShippingMeterForOrderConfirmation,
} from '../helpers';
import type { EventTracking } from '../types';
import { getOrderPlatformFromPath } from '../utils/getOrderPlatformFromPath';

const PAGE_REFERRER_KEY = 'ps_page_referrer';

const isOrderConfirmationPath = (pathname: string) => pathname.includes('/order-confirmation');

const isFreeShippingMeterPage = (pageType: string, pathname: string) =>
    isOrderConfirmationPath(pathname) || pageType === PageType.CART || pageType === PageType.CHECKOUT;

export const usePageViewTracking = () => {
    const pathname = usePathname();
    const origin = getOrigin();
    const { pageType, userData, user } = usePageLayout();
    const { memberPoints, isLoading } = useLoyaltyContext();
    const { isSoftAuthenticated } = useUserSession();
    const { autoshipData, isLoading: isAutoshipLoading } = useCustomerAutoshipSubscriptions();
    const cartContext = useContext(CartContext);
    const cart = cartContext?.cart ?? null;
    const isCartLoading = cartContext?.isLoading ?? false;

    const previousPathRef = useRef<string | null>(null);
    const hasTrackedPageRef = useRef(false);

    const enrichEventDataWithAutoship = (eventData: EventTracking) => {
        if (userData && user && autoshipData) {
            eventData.autoship_customer = autoshipData.data.hasAutoship;
            eventData.first_autoship_eligible = autoshipData.data.isFirstAutoshipEligible;
        }
    };

    const getPageReferrer = (): string => {
        if (globalThis.window === undefined) {
            return '';
        }
        return sessionStorage.getItem(PAGE_REFERRER_KEY) ?? '';
    };

    const resolveFreeShippingMeter = (currentPath: string): string | null => {
        if (!isFreeShippingMeterPage(pageType, currentPath)) {
            if (isFreeShippingMeterLockedForConfirmation()) {
                clearFreeShippingMeterStorage();
            }
            return null;
        }

        if (isOrderConfirmationPath(currentPath)) {
            const storedValue = getStoredFreeShippingMeterValue();
            // Release lock after confirmation consumes the snapshot so cart/checkout syncs are not blocked.
            unlockFreeShippingMeterForOrderConfirmation();
            return storedValue;
        }

        const value = getFreeShippingMeterAnalyticsValue(cart);
        syncFreeShippingMeterStorage(value);
        return value;
    };

    const firePageView = (currentPath: string) => {
        const cartId = Cookies.get(COOKIES.CART_ID);
        const mappedPageData = pageDataMapper(pageType, currentPath);
        const custom_device_id = Cookie.get('x-petm-visitor-id');
        const oktaIdToken = getOktaIdToken();
        const decodedAosJwt = oktaIdToken ? decodeJwt<{ email: string }>(oktaIdToken)?.payload : null;

        const loginStatus = isSoftAuthenticated ? 'soft' : Boolean(userData);

        // Determine platform based on order ID for order details pages
        const platform = getOrderPlatformFromPath(currentPath);
        const pageReferrer = getPageReferrer();
        const currentPageLocation = `${origin}${currentPath}`;

        const eventData: EventTracking = {
            event: 'page_view',
            page_location: currentPageLocation,
            page_title: document.title,
            page_referrer: pageReferrer,
            platform,
            is_aos_user: !!oktaIdToken,
            aos_email: oktaIdToken ? decodedAosJwt?.email : null,
            login_status: loginStatus,
            loyalty_id: user?.loyaltyid,
            treats_points: userData ? (memberPoints?.availablePoints ?? null) : null,
            email: userData?.profile?.emailAddress ?? null,
            treats_tier:
                userData && memberPoints?.currentTierRank ? `tier ${String(memberPoints?.currentTierRank)}` : null,
            cart_id: cartId,
            custom_device_id,
            autoship_customer: null,
            free_shipping_meter: resolveFreeShippingMeter(currentPath),
            ...mappedPageData,
        };

        enrichEventDataWithAutoship(eventData);

        dataLayerPush(eventData);

        // Save current page location as referrer for next page view
        if (globalThis.window !== undefined) {
            sessionStorage.setItem(PAGE_REFERRER_KEY, currentPageLocation);
        }

        if (mappedPageData.page_type === '404 error') {
            trackError(`${pathname}`, '404');
        }

        if (pageType === PageType.ACP) {
            // Use the same platform determination for event_accountmanagement
            dataLayerPush({
                event: 'event_accountmanagement',
                pageName: `ps:Account Management${currentPath
                    .split('/')
                    .map(str => str.split('-').join(' '))
                    .join(' ')}`,
                platform,
            });
        }
    };

    // Keep purchase-time meter value in sync while shopping; skip when locked for confirmation.
    useEffect(() => {
        if (isCartLoading || isOrderConfirmationPath(pathname ?? '')) {
            return;
        }

        syncFreeShippingMeterStorage(getFreeShippingMeterAnalyticsValue(cart));
    }, [cart, isCartLoading, pathname]);

    useEffect(() => {
        const hasAccessToken = !!Cookies.get(AUTH_COOKIES.ACCESS_TOKEN);

        // Skip if pathname is not available
        if (!pathname) {
            return;
        }

        const pathnameChanged = previousPathRef.current !== pathname;

        if (pathnameChanged) {
            if (previousPathRef.current !== null && globalThis.window !== undefined) {
                sessionStorage.setItem(PAGE_REFERRER_KEY, `${origin}${previousPathRef.current}`);
            }
            previousPathRef.current = pathname;
            hasTrackedPageRef.current = false;
        }

        const shouldWaitForData = hasAccessToken && (!userData || (!memberPoints && isLoading) || isAutoshipLoading);
        if (shouldWaitForData) {
            return;
        }

        const shouldWaitForCart =
            isFreeShippingMeterPage(pageType, pathname) && !isOrderConfirmationPath(pathname) && isCartLoading;
        if (shouldWaitForCart) {
            return;
        }

        if (!hasTrackedPageRef.current) {
            firePageView(pathname);
            hasTrackedPageRef.current = true;
        }
    }, [pathname, userData, memberPoints, isLoading, isAutoshipLoading, isCartLoading, cart, pageType]);
};
